import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { hazardService } from '../../../../../services/hazard.service';
import { message } from 'antd';
import HazardShape from './HazardShape';
import { parseMetadata } from './hazardUtils';

const Hazard = (props) => {
  const [hazards, setHazards] = useState([]);
  const [loading, setLoading] = useState(false);

  const filterVisibleHazards = React.useCallback((items = []) => {
    // 'resolved' is kept for legacy hazards; 'rectified' comes from the
    // Health-Safety activity integration. Both mean the hazard is done.
    const hiddenStatuses = new Set(['resolved', 'closed', 'rectified']);
    return _.filter(items, (hazard) => {
      // New hazards from the hazards table don't use the approval workflow,
      // so only require approval for legacy activity-based hazards.
      if (hazard?.latest_approval) {
        return hazard.latest_approval.status === 'approved';
      }
      // New hazards: show unless resolved/closed.
      const status = (hazard.status || '').toLowerCase();
      return !hiddenStatuses.has(status);
    });
  }, []);

  const updateDataHazards = React.useCallback((propertyId) => {
    if (!propertyId) return;
    setLoading(true);
    hazardService.getList(propertyId)
      .then(response => {
        if (response && response.code === '0000') {
          setHazards(filterVisibleHazards(response.hazards || []));
        } else {
          console.error('Failed to load hazards:', response);
          setHazards([]);
        }
      })
      .catch(error => {
        console.error('Error loading hazards:', error);
        message.error('Failed to load hazards');
        setHazards([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filterVisibleHazards]);

  useEffect(() => {
    if (!props.propertyId) return;
    updateDataHazards(props.propertyId);
  }, [props.propertyId, props.reloadKey, updateDataHazards]);

  const buildEditingPolicy = (hazard) => {
    let metadata = parseMetadata(hazard.metadata);

    if (!metadata.point) {
      metadata.point = { lat: hazard.lat, lng: hazard.lng };
    }

    if (!metadata.attachment_activity_id && hazard.attachment_activity_id) {
      metadata.attachment_activity_id = hazard.attachment_activity_id;
    }

    if (!metadata.attachments && hazard.attachments) {
      metadata.attachments = hazard.attachments;
    }

    metadata.hazard_category = metadata.hazard_category || hazard.hazard_category;
    metadata.hazard_type = metadata.hazard_type || hazard.hazard_type;
    metadata.hazard_type_other = metadata.hazard_type_other || hazard.hazard_type_other;
    metadata.actions = metadata.actions || hazard.actions;
    metadata.comments = metadata.comments || hazard.comments;

    return {
      id: hazard.policy_id || hazard.id,
      type: hazard.type || 'hazard',
      property_id: hazard.property_id,
      metadata,
      lat: hazard.lat,
      lng: hazard.lng,
      date_time: hazard.date_time || metadata.date_time || null,
      hazard_radius: hazard.hazard_radius || 10,
      isNewHazard: true,
      // Top-level hazard fields that the edit form hydrates directly — without
      // these, the details textarea, status, assignee, etc. render empty and
      // the user's saved values appear to "disappear" on reopen.
      description: hazard.description,
      hazard_category: hazard.hazard_category,
      hazard_type: hazard.hazard_type,
      hazard_type_other: hazard.hazard_type_other,
      status: hazard.status,
      is_closed: hazard.is_closed,
      actions_to_rectify: hazard.actions_to_rectify,
      allocated_to: hazard.allocated_to,
      rectification_due_date: hazard.rectification_due_date,
      deadline: hazard.deadline,
      workflow_state: hazard.workflow_state,
      current_escalation_level: hazard.current_escalation_level,
      // Polygon/circle geometry for editing
      shape_type: hazard.shape_type || 'marker',
      area: hazard.area || null,
      circle_lat: hazard.circle_lat || null,
      circle_lng: hazard.circle_lng || null,
      circle_radius: hazard.circle_radius || null,
    };
  };

  const handleEditHazard = (object, e) => {
    try {
      const shapeType = object.shape_type || 'marker';

      if (shapeType === 'polygon' || shapeType === 'rectangle') {
        // Extract updated coordinates from edited polygon/rectangle
        const latLngs = e.target.getLatLngs()[0];
        const updatedArea = latLngs.map(point => `${point.lat}, ${point.lng}`);
        props.handleSetUpdatedHazards(object, updatedArea);
      } else if (shapeType === 'circle') {
        // Extract updated center + radius from edited circle
        const center = e.target.getLatLng();
        const radius = e.target.getRadius();
        props.handleSetUpdatedHazards(object, {
          center: { lat: center.lat, lng: center.lng },
          radius: radius,
        });
      } else {
        // Marker drag — pass updated coords without mutating state object
        const LatLng = e.target.getLatLng();
        const updated = { ...object, lat: LatLng.lat, lng: LatLng.lng };
        props.handleSetUpdatedHazards(updated, LatLng);
      }
    } catch (error) {
      console.error('Error handling hazard edit:', error);
    }
  }

  useEffect(() => {
    if (typeof props.onHazardsLoaded === 'function') {
      props.onHazardsLoaded(hazards);
    }
  }, [hazards, props.onHazardsLoaded]);

  const handleShowHazards = (data) => {
    if (!data || data.length === 0) return [];
    return data.map((hazard) => (
      <HazardShape
        key={hazard.id}
        hazard={hazard}
        onClick={handleClickHazard.bind(this, hazard)}
        onEdit={handleEditHazard.bind(this, hazard)}
        onDragEnd={handleEditHazard.bind(this, hazard)}
        onRemove={handleRemoveHazard.bind(this, hazard)}
      />
    )).filter(Boolean);
  }

  const handleClickHazard = (object) => {
    // Guard against maps that don't wire an EditControl (e.g. read-only
    // MapHealthSafety). Without these optional chains the click handler
    // would throw on `controlDraw.current` being null and the marker would
    // silently fail to open the edit modal.
    const removeMode = props.controlDraw?.current?.leafletElement?._toolbars?.edit?._modes?.remove;
    if (removeMode?.handler?._enabled) {
      return;
    }

    if (typeof props.onOpenActivityModal === 'function') {
      const fallbackPoint = object && object.lat !== undefined && object.lng !== undefined
        ? {
            lat: object.lat,
            lng: object.lng,
            shape_type: object.shape_type || 'marker',
            data_area: object.area,
            circle_center: object.circle_lat && object.circle_lng
              ? { lat: object.circle_lat, lng: object.circle_lng }
              : undefined,
            circle_radius: object.circle_radius,
          }
        : null;
      props.onOpenActivityModal(buildEditingPolicy(object), fallbackPoint);
    }
  }

  const handleRemoveHazard = (hazard, e) => {
    if (props.handleSetDeletedHazards) {
      props.handleSetDeletedHazards(hazard.id);
    }
  }

  return (
    <>
      {handleShowHazards(hazards)}
    </>
  );
}

export default Hazard;
